import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown, FaCircleQuestion, FaHandsPraying } from "react-icons/fa6";

const HomeFaqs = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);

  const faqs = [
    { q: t('faq_q1'), a: t('faq_a1') },
    { q: t('faq_q2'), a: t('faq_a2') },
    { q: t('faq_q3'), a: t('faq_a3') },
    { q: t('faq_q4'), a: t('faq_a4') },
    { q: t('faq_q5'), a: t('faq_a5') },
    { q: t('faq_q6'), a: t('faq_a6') },
    { q: t('faq_q7', { defaultValue: 'How do I book a Panna Tiger Safari from Khajuraho?' }), a: t('faq_a7', { defaultValue: 'You can book online with us. Safaris run morning and afternoon from October to June. We arrange permits, gypsy and pickup from Khajuraho. Book 2-3 days early in season.' }) },
    { q: t('faq_q8', { defaultValue: 'How do I reach Khajuraho?' }), a: t('faq_a8', { defaultValue: 'Khajuraho has its own airport with flights from Delhi and Varanasi, plus rail links via Jhansi and Satna. We also arrange airport pickup and private taxis.' }) }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs
      .filter((f) => f.q && f.a)
      .map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a }
      }))
  };

  return (
    <section className="bg-white py-14 lg:py-24 relative overflow-hidden">
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      {/* Decorative background blur */}
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-orange-main/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          
          {/* LEFT SIDE: HEADER */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="sticky top-10"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="h-[2px] w-8 bg-orange-main" />
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-main">{t('faq_label')}</span>
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-black text-gray-dark tracking-tighter leading-tight mb-6">
              {t('faq_heading')}
            </h2>
            
            <p className="text-gray-500 text-base leading-relaxed mb-10 max-w-md font-medium">
              {t('faq_desc')}
            </p>

            {/* <div className="p-6 rounded-[2rem] bg-orange-main/5 border border-orange-main/10 inline-flex items-center gap-4">
               <div className="w-12 h-12 rounded-2xl bg-orange-main text-white flex items-center justify-center text-xl shadow-lg shadow-orange-main/20">
                  <FaHandsPraying />
               </div>
               <div>
                  <p className="text-sm font-bold text-gray-dark leading-none mb-1">Need more help?</p>
                  <p className="text-xs text-gray-500 font-medium">Our experts are a click away</p>
               </div>
            </div> */}
          </motion.div>

          {/* RIGHT SIDE: ACCORDION (Orange Theme Borders) */}
          <div className="space-y-4">
            {faqs.map((item, i) => {
              const isOpen = active === i;

              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className={`rounded-[1.5rem] transition-all duration-500 overflow-hidden border-2 ${
                    isOpen 
                      ? "bg-white shadow-[0_20px_50px_rgba(194,74,26,0.1)] border-orange-main" 
                      : "bg-white border-orange-main/10 hover:border-orange-main/30 hover:shadow-lg"
                  }`}
                >
                  <button
                    onClick={() => setActive(isOpen ? null : i)}
                    className="w-full flex justify-between items-center text-left px-8 py-6 group focus:outline-none outline-none border-none"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-2 h-2 rounded-full transition-all duration-500 ${isOpen ? "bg-orange-main scale-150" : "bg-gray-200"}`} />
                      <span className={`text-base font-bold tracking-tight transition-colors duration-500 ${isOpen ? "text-orange-main" : "text-gray-500"}`}>
                        {item.q}
                      </span>
                    </div>
                    <FaChevronDown className={`text-xs transition-transform duration-500 ${isOpen ? "rotate-180 text-orange-main" : "text-gray-400"}`} />
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
                      >
                        <div className="px-8 pb-8 pl-[3.5rem]">
                          <p className="text-gray-500 text-sm leading-relaxed font-medium">
                            {item.a}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

        </div>
      </div>
    </section>
  );
};

export default HomeFaqs;
